type AddressParts = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  /** Carried by a picked place; the line never shows it. */
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

/**
 * What the address input shows while she edits. Free text is hers verbatim,
 * spaces included, so typing is never reformatted under her fingers; once a
 * picked place fills the structured parts, it reads as one line.
 */
export function formatAddressDraft(address: AddressParts): string | null {
  const structured =
    address.address2 || address.city || address.postalCode || address.country;
  return structured ? formatAddressLine(address) : (address.address1 ?? null);
}

/**
 * One display line for an address record. address1 is whatever Places gave
 * us, and for anything short of a street it is the whole formatted address —
 * "Paris, France" — so appending the city after it would read back as
 * "Paris, France, Paris". A part already named in the line before it is
 * dropped rather than repeated; this is display only.
 *
 * The country is not part of the line. It is an ISO code in the record, the
 * field names it underneath by name and flag, and a record that holds only a
 * country — the sign-in default — shows an empty address, which is the truth.
 */
export function formatAddressLine(address: AddressParts): string | null {
  return (
    [address.address1, address.address2, address.city, address.postalCode]
      .filter((part): part is string => !!part && !!part.trim())
      .reduce<string[]>((parts, part) => {
        const alreadySaid = parts.some((kept) =>
          kept.toLowerCase().includes(part.toLowerCase()),
        );
        return alreadySaid ? parts : [...parts, part.trim()];
      }, [])
      .join(", ") || null
  );
}
